"use client";

import { useRef, useState } from "react";
import { flushSync } from "react-dom";
import GradientText from "@/components/GradientText";
import { REGISTER_URL } from "@/components/pricing/data";
import {
  Button,
  DARK_BAND,
  Eyebrow,
  SectionShell,
  Terminal,
  useBandRowAlign,
  useWholeRowBlocks,
} from "@/components/sections/kit";
import { gsap, NOT_REDUCED, useGSAP, useMaskedReveal } from "@/components/sections/kit/motion";
import { LIVE_COUNT, TOTAL_COUNT } from "./data";

/** Where each recorded click can be read — the columns of the ledger. Every
 *  human click lands in the dashboard (analytics + request logs, which MCP
 *  reads too); only clicks on links created through the public API also go
 *  out on the SSE stream (GET /v1/events/stream). Each name is also the key
 *  its counter pill increments under. */
const DESTINATIONS = [
  { name: "Dashboard", color: "var(--color-data-green)" },
  { name: "SSE stream", color: "var(--color-data-cyan)" },
] as const;

type DestName = (typeof DESTINATIONS)[number]["name"];

/** Base counter values. Synthetic by design — the pills are captioned
 *  "live · synthetic sample" and never claim to be an account's real traffic. */
const BASE_COUNTS: Record<DestName, number> = {
  Dashboard: 2861,
  "SSE stream": 1204,
};

interface PoolEvent {
  type: string;
  color: string;
  slug: string;
  geo: string;
  dests: DestName[];
}

/** The synthetic event pool — eight events cycling forever. Deterministic on
 *  purpose (no Math.random, no real clock): SSR and hydration must agree, and
 *  the playbook forbids randomness in render paths. The fields mirror the
 *  real click payload: one `click` event type (QR scans carry is_qr), a
 *  country code and a device type — no city. */
const POOL: PoolEvent[] = [
  { type: "click · qr", color: "var(--color-data-green)", slug: "/berlin-flyer", geo: "DE · mobile", dests: ["Dashboard", "SSE stream"] },
  { type: "click", color: "var(--color-data-blue)", slug: "/renew", geo: "FR · desktop", dests: ["Dashboard"] },
  { type: "click", color: "var(--color-data-blue)", slug: "/launch", geo: "US · mobile", dests: ["Dashboard", "SSE stream"] },
  { type: "click", color: "var(--color-data-blue)", slug: "/summer-sale", geo: "ES · mobile", dests: ["Dashboard"] },
  { type: "click · qr", color: "var(--color-data-green)", slug: "/unbox", geo: "AT · mobile", dests: ["Dashboard", "SSE stream"] },
  { type: "click", color: "var(--color-data-blue)", slug: "/docs", geo: "GB · desktop", dests: ["Dashboard"] },
  { type: "click", color: "var(--color-data-blue)", slug: "/pricing", geo: "CH · tablet", dests: ["Dashboard", "SSE stream"] },
  { type: "click · qr", color: "var(--color-data-green)", slug: "/menu-qr", geo: "DK · mobile", dests: ["Dashboard"] },
];

/** Rows visible at once. The ledger body is height-locked to this count so a
 *  new arrival never moves the layout below it. */
const VISIBLE_ROWS = 7;
/** Seconds between arrivals — the design's pacing. */
const BEAT = 2.1;

interface Row extends PoolEvent {
  id: number;
  time: string;
}

/** Row `n` of the stream. Timestamps advance three seconds per event. */
const mk = (n: number): Row => {
  const p = POOL[n % POOL.length];
  const t = 2 + n * 3;
  return { ...p, id: n, time: `09:14:${String(t % 60).padStart(2, "0")}` };
};

/** The resting state: seven rows already on the wire, newest first. This is
 *  also exactly what reduced-motion and mobile visitors get. */
const SEED: Row[] = Array.from({ length: VISIBLE_ROWS }, (_, i) => mk(VISIBLE_ROWS - 1 - i));

interface Feed {
  rows: Row[];
  counts: Record<DestName, number>;
  next: number;
}

const INITIAL: Feed = { rows: SEED, counts: BASE_COUNTS, next: VISIBLE_ROWS };

const SR_SUMMARY =
  "Illustrative click feed: rows of clicks and QR scans, each showing the time, the event type, the " +
  "short link, the visitor's country and device, and where it can be read — the dashboard for every " +
  "link, plus the live SSE stream for links created through the API. The sample is synthetic; the " +
  "columns are the fields a real click event carries.";

/** Grid template for one ledger line. Geo only appears once there is room for
 *  it (lg+); below md the row stops being a grid entirely and wraps. */
const ROW_GRID =
  "md:grid md:grid-cols-[70px_84px_minmax(0,1fr)_170px] md:gap-[14px] lg:grid-cols-[78px_92px_minmax(0,1fr)_130px_210px]";

/**
 * /integrations signature — the LIVE LEDGER.
 *
 * A dark content-height hero whose instrument is a `tail -f events` terminal:
 * clicks stream in at the top, one every 2.1s, and the counter pills above
 * tick up for each place the click can be read (the dashboard for every
 * link, the SSE stream for API-created links). That fan-out IS the page's
 * argument — an integration is "this event, arriving over there" — so the
 * device aids comprehension rather than decorating.
 *
 * Motion contract (playbook §Motion): ONE paused GSAP timeline drives the
 * feed — a finite eight-beat pass, completion-chained `restart()` (never
 * `repeat:-1`), state writes via `flushSync` inside `tl.call`, arrival tween
 * fired imperatively right after. IntersectionObserver-paused off-screen,
 * beats skipped while `document.hidden`. `insure()` is deliberately NOT armed
 * (one-shot contract, illegal on a restarting loop). Under reduced motion or
 * below md nothing runs and the seeded seven rows are the resting state.
 */
export default function IntegrationsLedgerHero() {
  const [feed, setFeed] = useState<Feed>(INITIAL);

  const copyBlockRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  const instrumentRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  useWholeRowBlocks(copyBlockRef);
  useBandRowAlign(copyBlockRef);
  useMaskedReveal(copyRef, { heading: "h1", sub: "[data-hero-sub]" });

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(`${NOT_REDUCED} and (min-width: 768px)`, () => {
        const push = () => {
          // A hidden tab gets no arrivals — matching the design's pause, and
          // keeping the counters honest about elapsed "traffic".
          if (document.hidden) return;
          flushSync(() =>
            setFeed((f) => {
              const row = mk(f.next);
              const counts = { ...f.counts };
              row.dests.forEach((d) => {
                counts[d] += 1;
              });
              return { rows: [row, ...f.rows].slice(0, VISIBLE_ROWS), counts, next: f.next + 1 };
            }),
          );
          // Imperative arrival tween on the node React just prepended — the
          // sanctioned idiom for feedback that follows a synchronous commit.
          const first = bodyRef.current?.firstElementChild;
          if (first) {
            gsap.from(first, {
              y: -8,
              opacity: 0,
              duration: 0.35,
              ease: "power2.out",
              clearProps: "opacity,transform",
            });
          }
        };

        const tl = gsap.timeline({ paused: true, defaults: { immediateRender: false } });
        // One finite pass over the whole pool, then a hold so the last arrival
        // can be read before the chained restart.
        for (let i = 0; i < POOL.length; i++) tl.call(push, undefined, `+=${BEAT}`);
        tl.to({}, { duration: BEAT });

        let visible = false;
        // Chain the next pass off completion (never `repeat:-1` — the wrap
        // edge of a `.call`-heavy timeline stalls in live browsers). The tiny
        // real-clock hold keeps `restart()` out of GSAP's own onComplete
        // callstack, the Switchboard/Forge idiom.
        let hold = 0;
        const replay = () => {
          window.clearTimeout(hold);
          hold = window.setTimeout(() => {
            if (visible) tl.restart();
          }, 60);
        };
        tl.eventCallback("onComplete", replay);

        const io = new IntersectionObserver(
          (entries) =>
            entries.forEach((e) => {
              visible = e.isIntersecting;
              if (!e.isIntersecting) {
                tl.pause();
                return;
              }
              if (tl.progress() === 1) tl.restart();
              else tl.play();
            }),
          { threshold: 0.15 },
        );
        if (instrumentRef.current) io.observe(instrumentRef.current);

        return () => {
          io.disconnect();
          window.clearTimeout(hold);
          tl.eventCallback("onComplete", null);
          tl.kill();
        };
      });
    },
    { scope: instrumentRef },
  );

  return (
    <SectionShell
      id="ref-ledger-hero"
      band="dark"
      decoAvoid
      className="max-md:bg-[#14141c] md:pt-px md:pb-px"
    >
      <div className="pt-24 pb-16 md:pb-0 md:pt-[calc(3*var(--grid-cell,72px))]">
        {/* ── Block 1 — the claim. ───────────────────────────────────── */}
        <div ref={copyBlockRef} data-grid-block data-grid-fill={DARK_BAND}>
          <div
            ref={copyRef}
            className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between md:gap-12"
          >
            <div className="min-w-0">
              <div data-hero-sub className="flex">
                <Eyebrow tone="light">Connects to your code</Eyebrow>
              </div>
              <h1 className="mt-[22px] max-w-[760px] text-[clamp(34px,5.4vw,54px)] font-extrabold leading-[1.04] tracking-[-0.03em] text-white">
                Every event,{" "}
                <span className="whitespace-nowrap">
                  <GradientText colors={["#f55c88", "#fa437f", "#f55c88"]} animationSpeed={3} yoyo={false}>
                    on the record
                  </GradientText>
                  .
                </span>
              </h1>
              <p
                data-hero-sub
                className="mt-[18px] max-w-[560px] text-[17px] leading-[1.6] text-[#a6a6b8] [text-wrap:pretty]"
              >
                Clicks and scans land on the record the moment they happen — in your
                dashboard for every link, and live into your code for links you
                create over the API. This is what the wire looks like.
              </p>
            </div>

            <div data-hero-sub className="flex flex-none flex-wrap gap-3 md:pb-1.5">
              <Button href={REGISTER_URL} variant="inverse">
                Start free
              </Button>
              <Button href="#catalog" variant="ghost-light">
                Browse the catalog
              </Button>
            </div>
          </div>
        </div>

        {/* ── Block 2 — the instrument. ──────────────────────────────── */}
        {/* max-md:mt-10 — on md+ the row aligner owns this block's margin-top;
            below md it clears inline margins, so the phone stack needs its
            own breathing room. */}
        <div ref={instrumentRef} data-grid-block data-grid-fill={DARK_BAND} className="max-md:mt-10">
          {/* Counter pills — one per destination, ticking with the feed. */}
          <div className="flex flex-wrap items-center gap-2.5">
            {DESTINATIONS.map((d) => (
              <div
                key={d.name}
                className="flex items-baseline gap-2 rounded-full border border-white/[0.12] px-3.5 py-2"
              >
                <span
                  aria-hidden
                  className="h-1.5 w-1.5 self-center rounded-full"
                  style={{ background: d.color }}
                />
                <span className="text-[13px] font-semibold text-white">{d.name}</span>
                <span
                  className="font-jetbrains text-[12px] tabular-nums"
                  style={{ color: d.color }}
                >
                  {feed.counts[d.name].toLocaleString("en-US")}
                </span>
              </div>
            ))}
            <span className="ml-auto inline-flex items-center gap-[7px] self-center font-jetbrains text-[11px] uppercase tracking-[0.06em] text-[#73738a]">
              <span
                aria-hidden
                className="h-[7px] w-[7px] animate-status-pulse rounded-full bg-data-green"
              />
              live · synthetic sample
            </span>
          </div>

          {/* The ledger. */}
          <div className="mt-4">
            <Terminal path="rii — tail -f events">
              <p className="sr-only">{SR_SUMMARY}</p>
              <div aria-hidden className="font-jetbrains text-[12px]">
                <div
                  className={`${ROW_GRID} hidden border-b border-white/10 pb-2.5 pt-0.5 text-[10px] uppercase tracking-[0.09em] text-[#73738a]`}
                >
                  <span>time</span>
                  <span>event</span>
                  <span>link</span>
                  <span className="hidden lg:block">country · device</span>
                  <span>readable in</span>
                </div>
                <div ref={bodyRef} className="md:h-[252px] md:overflow-hidden">
                  {feed.rows.map((r, i) => (
                    <div
                      key={r.id}
                      className={`${ROW_GRID} flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-white/5 py-2 max-md:border-white/[0.07] ${
                        i > 4 ? (i === 5 ? "opacity-60" : "opacity-30") : ""
                      } ${i >= 5 ? "max-md:hidden" : ""}`}
                    >
                      <span className="text-[#73738a]">{r.time}</span>
                      <span style={{ color: r.color }}>{r.type}</span>
                      <span className="min-w-0 flex-1 truncate text-[#e8e8f0]">
                        <span className="text-[#73738a]">rii.link</span>
                        {r.slug}
                      </span>
                      <span className="hidden text-[#a6a6b8] lg:block">{r.geo}</span>
                      <span className="flex basis-full gap-[5px] overflow-hidden md:basis-auto">
                        {r.dests.map((d) => (
                          <span
                            key={d}
                            className="whitespace-nowrap rounded-full border border-white/10 bg-white/[0.07] px-2 py-0.5 text-[10px] text-[#c9c9d6]"
                          >
                            {d}
                          </span>
                        ))}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </Terminal>
          </div>

          <p className="mt-[18px] font-jetbrains text-[11.5px] text-[#73738a]">
            {TOTAL_COUNT} connections · {LIVE_COUNT} live · anything else via the REST
            API or MCP —{" "}
            <a href="#catalog" className="text-[#c9c9d6] underline-offset-2 hover:underline">
              see the full catalog ↓
            </a>
          </p>
        </div>
      </div>
    </SectionShell>
  );
}
